import { uniq } from "lodash";
import { IStore, StoreContext } from "store/store-context";
import { IProcessedTag, ITagger, TaggerDocument, TagTableRow } from "store/types/tagger";
import { useContextSelector } from "use-context-selector";

//YOUR SELECTORS HERE

export const useTaggerSelector = (): ITagger => useContextSelector(StoreContext, (store: IStore) => store.tagger);

export const useTaggerDealIdSelector = (): number | null =>
  useContextSelector(StoreContext, (store: IStore) => {
    const dealIds = store.tagger.documentIds.map((docId) => store.tagger.documents[docId].dealId);
    const uniqueDeals = uniq(dealIds);
    return uniqueDeals.length === 1 ? uniqueDeals[0] : null;
  });

export const useTaggerDealIdsSelector = (): number[] =>
  useContextSelector(StoreContext, (store: IStore) => {
    const dealIds = store.tagger.documentIds.map((docId) => store.tagger.documents[docId].dealId);
    return uniq(dealIds);
  });

// export const useTaggerInvestorSelector = (docId: string): number | null =>
//   useContextSelector(StoreContext, (store: IStore) => store.tagger.documents[docId]?.selectedInvestor || null);
//
// export const useTaggerRoundSelector = (docId: string): number | null =>
//   useContextSelector(StoreContext, (store: IStore) => store.tagger.documents[docId]?.selectedRound || null);
//
// export const useTaggerDocumentDataSelector = (docId: string, field: DocumentDataField) =>
//   useContextSelector(StoreContext, (store: IStore) => store.tagger.documents[docId]?.[field] || null);

export const useTaggerDocumentArraySelector = (): TaggerDocument[] =>
  useContextSelector(StoreContext, (store: IStore) => store.tagger.documentIds.map((id) => store.tagger.documents[id]));

export const useTaggerDocumentIdsSelector = (): TaggerDocument["id"][] =>
  useContextSelector(StoreContext, (store: IStore) => store.tagger.documentIds);

export const useTaggerDocumentSelector = (docId: number): TaggerDocument =>
  useContextSelector(StoreContext, (store: IStore) => store.tagger.documents[docId]);

export const useTaggerDocumentTagsSelector = (
  docId: number
): IProcessedTag[] => //added children tags with documentId in case to mark it green on document rectangle
  useContextSelector(StoreContext, (store: IStore) => {
    const getAllTagsForDocument = (tags: IProcessedTag[]): IProcessedTag[] => {
      return tags.reduce<IProcessedTag[]>((acc, tag) => {
        const matchingTags: IProcessedTag[] = [];

        if (tag.document_id === docId && !tag.deleted_at) {
          matchingTags.push(tag);
        }

        if (tag.children?.length) {
          matchingTags.push(...getAllTagsForDocument(tag.children));
        }

        return [...acc, ...matchingTags];
      }, []);
    };

    return getAllTagsForDocument(store.tagger.tags);
  });

export const useTaggerDocumentAllTagsSelector = (): IProcessedTag[] =>
  useContextSelector(StoreContext, (store: IStore) => store.tagger.tags);

export const useTaggerGeneralTagsSelector = (): IProcessedTag[] =>
  useContextSelector(StoreContext, (store: IStore) => store.tagger.tags.filter((tag) => !tag.document_id));

export const useTaggerTagByIdSelector = (
  tagId: number,
  isChild: boolean,
  hasChildren: boolean
): IProcessedTag | undefined =>
  useContextSelector(StoreContext, (store: IStore) => {
    // If looking for a top-level parent, find it directly
    if (!isChild) {
      return store.tagger.tags.find((tag) => tag.id === tagId);
    }

    // Find all child tags with matching ID (tags that are children of some parent)
    const findChildTags = (tags: IProcessedTag[], depth: number = 0): Array<{ tag: IProcessedTag; depth: number }> => {
      const matches: Array<{ tag: IProcessedTag; depth: number }> = [];

      for (const tag of tags) {
        // Only include tags that are children (depth > 0) and match the ID
        if (tag.id === tagId && depth > 0) {
          matches.push({ tag, depth });
        }

        // Recursively search in children
        if (tag.children) {
          matches.push(...findChildTags(tag.children, depth + 1));
        }
      }

      return matches;
    };

    const childMatches = findChildTags(store.tagger.tags);

    if (childMatches.length === 0) return undefined;
    if (childMatches.length === 1) return childMatches[0].tag;

    // Separate parent nodes (with children) from leaf nodes (no children)
    const parentNodes = childMatches.filter(({ tag }) => tag.children && tag.children.length > 0);
    const leafNodes = childMatches.filter(({ tag }) => !tag.children || tag.children.length === 0);

    // Use hasChildren to determine which tag to return
    // The returned tag must match both isChild and hasChildren conditions
    if (hasChildren) {
      // User clicked on a row with children - return the shallowest parent node
      if (parentNodes.length > 0) {
        return parentNodes.reduce((shallowest, current) => (current.depth < shallowest.depth ? current : shallowest))
          .tag;
      }
      // Fallback: if no parent nodes found, return first match
      return childMatches[0].tag;
    } else {
      // User clicked on a leaf row - return the deepest leaf node
      if (leafNodes.length > 0) {
        return leafNodes.reduce((deepest, current) => (current.depth > deepest.depth ? current : deepest)).tag;
      }
      // Fallback: if no leaf nodes found, return first match
      return childMatches[0].tag;
    }
  });

export const useTaggerDocumentParsingSelector = (): boolean =>
  useContextSelector(
    StoreContext,
    (store: IStore) =>
      !!store.tagger.documentIds.length &&
      store.tagger.documentIds.some((docId) => {
        const doc = store.tagger.documents[docId];
        return !doc.polygons && !doc.error;
      })
  );

export const useTaggerAnalyzingStatusSelector = (): IStore["tagger"]["analyzingStatus"] =>
  useContextSelector(StoreContext, (store: IStore) => store.tagger.analyzingStatus);

export const useTaggerTagTableSelector = (): TagTableRow[] => {
  return useContextSelector(StoreContext, (store: IStore) => {
    const getFileName = (docId: number) => {
      if (store.tagger.documentIds.includes(docId)) return store.tagger.documents[docId].file.name;
      return "-";
    };

    const mapTagToTableRow = (tag: IProcessedTag, level: number = 0): TagTableRow => ({
      ...tag,
      filename: getFileName(tag.document_id || 0),
      nested_level: level,
      enabled: !tag.document_id || store.tagger.documentIds.includes(tag.document_id),
      children: tag.children?.map((child) => mapTagToTableRow(child, level + 1)) || null
    });
    return store.tagger.tags.map((tag) => mapTagToTableRow(tag));
  });
};
